import { useCallback, useState, type FormEvent } from 'react'
import type { InviteView } from '@olmt/shared'
import { api, describeError, type AdminUser } from '../lib/api'
import { WEB_ORIGIN } from '../lib/env'
import { formatStamp } from '../lib/format'
import { useLoad } from '../hooks/useLoad'
import { CopyButton } from '../components/CopyButton'
import { IconButton } from '../components/IconButton'
import { CheckIcon, PlusIcon, RefreshIcon, TrashIcon } from '../components/Icons'
import { SectionHeading } from '../components/SectionHeading'
import { Spinner } from '../components/Spinner'

type InvitesData = { invites: InviteView[]; names: Map<string, AdminUser> }

/**
 * Invites: the family's fast lane. `POST /api/admin/invites` mints one, `DELETE` revokes it, and
 * a recovery code (`invites.user_id` set) shows up in the same list bound to its member
 * (docs/CONTRACT.md §4). Redeeming one lands the account `approved`, so a code is the way past the
 * queue.
 */
export function InvitesPage() {
  const load = useCallback(async (): Promise<InvitesData> => {
    const [invites, users] = await Promise.all([api.adminInvites(), api.adminUsers()])
    const names = new Map<string, AdminUser>()
    for (const user of users.users) names.set(user.id, user)
    return { invites: invites.invites, names }
  }, [])
  const { data, loading, error, reload } = useLoad(load, (caught) =>
    describeError(caught, 'Could not load the invites.'),
  )

  const [label, setLabel] = useState('')
  const [maxUses, setMaxUses] = useState('1')
  const [expiresInHours, setExpiresInHours] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [creating, setCreating] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [created, setCreated] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const create = async (event: FormEvent) => {
    event.preventDefault()
    if (creating) return
    setCreating(true)
    setFormError(null)
    try {
      const response = await api.createInvite({
        label: label.trim() || undefined,
        isAdmin,
        maxUses: Number(maxUses) > 0 ? Number(maxUses) : 1,
        expiresInHours: Number(expiresInHours) > 0 ? Number(expiresInHours) : undefined,
      })
      setCreated(response.url)
      setLabel('')
      setIsAdmin(false)
      setMaxUses('1')
      setExpiresInHours('')
      reload()
    } catch (caught) {
      setFormError(describeError(caught, 'Could not create the invite.'))
    } finally {
      setCreating(false)
    }
  }

  const revoke = async (inviteId: string) => {
    setBusyId(inviteId)
    setActionError(null)
    try {
      await api.deleteInvite(inviteId)
      setConfirmId(null)
      reload()
    } catch (caught) {
      setActionError(describeError(caught, 'Could not revoke that invite.'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section aria-labelledby="invites-heading">
      <SectionHeading
        id="invites-heading"
        action={
          <IconButton label="Refresh the invites" size={36} onClick={reload} disabled={loading}>
            <RefreshIcon size={16} />
          </IconButton>
        }
      >
        Invites
        {data && <span className="numeric ms-2 text-muted">{data.invites.length}</span>}
      </SectionHeading>

      <form onSubmit={create} className="rounded-panel bg-surface p-3.5 ring-1 ring-line ring-inset">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-meta text-muted">
            Label
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              maxLength={64}
              placeholder="e.g. Aunt Mary"
              className="min-h-11 rounded-panel bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset placeholder:text-muted"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-meta text-muted">
            Max uses
            <input
              type="number"
              min={1}
              value={maxUses}
              onChange={(event) => setMaxUses(event.target.value)}
              className="numeric min-h-11 rounded-panel bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-meta text-muted">
            Expires in hours (optional)
            <input
              type="number"
              min={1}
              value={expiresInHours}
              onChange={(event) => setExpiresInHours(event.target.value)}
              className="numeric min-h-11 rounded-panel bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset"
            />
          </label>
          <label className="flex min-h-11 items-center gap-2.5 text-ui text-ink">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(event) => setIsAdmin(event.target.checked)}
            />
            Grant admin
          </label>
        </div>
        <button
          type="submit"
          disabled={creating}
          className="press mt-3.5 flex min-h-11 items-center gap-2 rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
        >
          {creating ? <Spinner size={16} label="Creating" /> : <PlusIcon size={18} />}
          Create invite link
        </button>
        {formError && (
          <p role="alert" className="mt-2 text-meta text-danger">
            {formError}
          </p>
        )}
        {created && (
          <p className="mt-3 flex items-center gap-2 rounded-panel border-s-2 border-accent bg-surface2 py-1.5 ps-3 pe-1.5 text-meta text-ink">
            <span className="min-w-0 flex-1 break-all">
              <bdi>{created}</bdi>
              <span className="block text-muted">
                Anyone who redeems this link joins approved, skipping the queue.
              </span>
            </span>
            <CopyButton value={created} label="Copy invite link" />
          </p>
        )}
      </form>

      {loading && !data && (
        <div className="flex justify-center py-10 text-muted">
          <Spinner label="Loading invites" />
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-ui text-danger">
          {error}
        </p>
      )}

      {actionError && (
        <p role="alert" className="mt-3 text-ui text-danger">
          {actionError}
        </p>
      )}

      {data && data.invites.length === 0 && (
        <p className="mt-3 rounded-panel bg-surface p-5 text-ui text-muted ring-1 ring-line ring-inset">
          No invites yet. Create one to let someone join without waiting in the queue.
        </p>
      )}

      {data && data.invites.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {data.invites.map((invite) => {
            const revoked = invite.revokedAt !== null
            const exhausted = invite.uses >= invite.maxUses
            const expired = invite.expiresAt !== null && invite.expiresAt < Date.now()
            const spent = revoked || exhausted || expired
            const bound = invite.userId !== null ? data.names.get(invite.userId) : undefined
            const url = WEB_ORIGIN ? `${WEB_ORIGIN}/i/${invite.code}` : null
            return (
              <li
                key={invite.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-panel bg-surface p-3 ring-1 ring-line ring-inset"
              >
                <span className={`min-w-0 flex-1 ${spent ? 'opacity-60' : ''}`}>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="truncate text-ui text-ink">
                      <bdi>{invite.label ?? 'Invite'}</bdi>
                    </span>
                    {invite.isAdmin && (
                      <span className="shrink-0 rounded-full bg-accent/12 px-2 py-0.5 text-meta font-medium text-accent ring-1 ring-accent/30 ring-inset">
                        admin
                      </span>
                    )}
                    {bound && (
                      <span className="shrink-0 rounded-full bg-surface2 px-2 py-0.5 text-meta font-medium text-muted ring-1 ring-line-strong ring-inset">
                        recovery for <bdi>{bound.displayName}</bdi>
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-meta text-muted">
                    <span className="numeric">
                      {invite.uses}/{invite.maxUses} uses
                    </span>
                    {` · created ${formatStamp(invite.createdAt)}`}
                    {invite.expiresAt !== null && ` · expires ${formatStamp(invite.expiresAt)}`}
                    {revoked && ' · revoked'}
                    {!revoked && exhausted && ' · used up'}
                    {!revoked && expired && ' · expired'}
                  </span>
                </span>

                {url ? (
                  <CopyButton value={url} label="Copy invite link" />
                ) : (
                  <span className="text-meta text-muted" title="Set VITE_WEB_ORIGIN to copy shareable links">
                    code <span className="numeric text-ink">{invite.code}</span>
                  </span>
                )}

                {!revoked &&
                  (confirmId === invite.id ? (
                    <span className="flex items-center gap-1">
                      <IconButton
                        label="Confirm revoke"
                        variant="danger"
                        size={40}
                        disabled={busyId !== null}
                        onClick={() => void revoke(invite.id)}
                      >
                        {busyId === invite.id ? <Spinner size={16} label="Revoking" /> : <CheckIcon size={18} />}
                      </IconButton>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="press min-h-11 rounded-full px-3 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <IconButton
                      label="Revoke invite"
                      variant="destructive"
                      size={40}
                      onClick={() => setConfirmId(invite.id)}
                    >
                      <TrashIcon size={18} />
                    </IconButton>
                  ))}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
